import { cur } from '../lib/units';
import { useState } from 'react';
import { askConfirm } from '../lib/askConfirm';
import { feat } from '../lib/features';
import { getMode, PROFILES } from '../lib/profiles';
import { createPortal } from 'react-dom';
import { X, Tags, RotateCcw, Shovel, Boxes } from 'lucide-react';
import useCatalog from '../hooks/useCatalog';
import { matchItems, inSector } from '../lib/catalog';
import { getGlobalPitPrices, setProjectPitPrices, PIT_MATS, MAT_UNITS, unitLabel } from '../lib/excavation';

const numVal = (v) => (v === '' ? null : +String(v).replace(',', '.'));
const clean = (o) => Object.fromEntries(Object.entries(o).filter(([, v]) => v.price != null || v.install != null));

export const countProjectPrices = (project) => Object.keys(project.prices?.items || {}).length + Object.values(project.prices?.pits || {}).filter((v) => v.price != null).length;

export default function ProjectPrices({ t, project, updateProject, onClose }) {
  const cat = useCatalog();
  const [q, setQ] = useState('');
  const pr = project.prices || {};
  const items = pr.items || {}, pits = pr.pits || {};
  const glob = getGlobalPitPrices();
  const save = (next) => { setProjectPitPrices(next.pits); updateProject((p) => ({ ...p, prices: next })); };
  const setItem = (id, k, v) => save({ ...pr, items: clean({ ...items, [id]: { ...items[id], [k]: numVal(v) } }) });
  const setPit = (k, patch) => save({ ...pr, pits: { ...pits, [k]: { ...pits[k], ...patch } } });
  const reset = async () => (await askConfirm(t.ppResetConfirm, { cancel: t.btnCancel })) && save({});
  const sec = PROFILES[getMode()]?.sector, pitsOn = feat('pits') && sec !== 'building' && sec !== 'garden';
  const list = matchItems(cat.items.filter((x) => inSector(x, sec)), q, '');
  return createPortal(
    <div className="editor-backdrop" data-testid="project-prices-dialog">
      <div className="editor pp-dialog">
        <div className="editor-head">
          <h4><Tags size={18} /> {t.ppTitle}</h4>
          <button className="icon-btn" data-testid="project-prices-close-btn" onClick={onClose}><X size={16} /></button>
        </div>
        <p className="muted small-hint">{t.ppHint}</p>
        {pitsOn && <b className="mat-sub"><Shovel size={14} /> {t.pitPrices}</b>}
        {pitsOn && PIT_MATS.map((k) => (
          <div className="row price-row" key={k} data-testid={`pp-pit-row-${k}`}>
            <span className="price-name">{t['pitM_' + k]}</span>
            <input type="number" step="0.01" inputMode="decimal" data-testid={`pp-pit-price-${k}`} className={pits[k]?.price != null ? 'overridden' : ''} placeholder={glob[k].price != null ? String(glob[k].price) : `${cur()}`}
              value={pits[k]?.price ?? ''} onChange={(e) => setPit(k, { price: numVal(e.target.value) })} />
            <select data-testid={`pp-pit-unit-${k}`} value={pits[k]?.unit || glob[k].unit} onChange={(e) => setPit(k, { unit: e.target.value })}>
              {MAT_UNITS[k].map((u) => <option key={u} value={u}>{`${cur()}/${unitLabel(u)}`}</option>)}
            </select>
          </div>
        ))}
        <b className="mat-sub"><Boxes size={14} /> {t.catTitle}</b>
        <input data-testid="pp-search-input" value={q} placeholder={t.catSearch} onChange={(e) => setQ(e.target.value)} />
        <div className="pp-head"><span>{t.catName}</span><span>{t.catPrice}</span><span>{t.catInstall}</span></div>
        <div className="cat-rows pp-rows" data-testid="pp-item-list">
          {list.map((x) => (
            <div key={x.id} className="pp-row" data-testid={`pp-item-${x.id}`}>
              <span className="cat-row-name"><b>{x.name}</b><small>{[x.dim, t['unit_' + x.unit]].filter(Boolean).join(' · ')}</small></span>
              <input type="number" step="0.01" inputMode="decimal" data-testid={`pp-item-price-${x.id}`} className={items[x.id]?.price != null ? 'overridden' : ''} placeholder={x.price != null ? String(x.price) : '–'}
                value={items[x.id]?.price ?? ''} onChange={(e) => setItem(x.id, 'price', e.target.value)} />
              <input type="number" step="0.01" inputMode="decimal" data-testid={`pp-item-install-${x.id}`} className={items[x.id]?.install != null ? 'overridden' : ''} placeholder={x.install != null ? String(x.install) : '–'}
                value={items[x.id]?.install ?? ''} onChange={(e) => setItem(x.id, 'install', e.target.value)} />
            </div>
          ))}
        </div>
        <div className="row">
          <button className="btn btn-outline btn-sm" data-testid="pp-reset-btn" onClick={reset}><RotateCcw size={14} /> {t.ppReset}</button>
          <button className="btn btn-green btn-sm" data-testid="pp-done-btn" onClick={onClose}>{t.ppDone}</button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
